import { useEffect, useRef, useState } from 'react';
import { Check, Moon, Sun } from 'lucide-react';
import { ACCENT_PRESETS, DEFAULT_ACCENT_COLORS, type AccentColors } from '../shared/accent-colors';
import type { AppearanceMode } from '../shared/desktop-api';
import { hostHas } from '../shared/host-capabilities';
import { TooltipButton } from './TooltipButton';
import { paigeAccentPresets } from '../paige/paige-theme';

/** The one failure to read here; every other message is a color that was not saved. */
const COLORS_UNREADABLE = 'Could not read your colors. Try reopening Appearance.';

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function AccentSettings() {
  const [colors, setColors] = useState<AccentColors>(DEFAULT_ACCENT_COLORS);
  const [error, setError] = useState('');
  const pending = useRef(0);
  useEffect(() => {
    const desktop = window.capturePointDesktop;
    if (!desktop?.getAccentColors) return;
    let alive = true;
    const receive = (value: AccentColors) => {
      if (alive && pending.current === 0) setColors(value);
    };
    const unsubscribe = desktop.onAccentColorsChanged(receive);
    void desktop
      .getAccentColors()
      .then(receive)
      .catch(() => {
        if (alive) setError(COLORS_UNREADABLE);
      });
    return () => {
      alive = false;
      unsubscribe();
    };
  }, []);
  const choose = async (mode: AppearanceMode, color: string) => {
    const desktop = window.capturePointDesktop;
    if (!desktop?.setAccentColor) {
      setError('Accent colors are available in the desktop app.');
      return;
    }
    pending.current++;
    const before = colors;
    setColors((previous) => ({ ...previous, [mode]: color }));
    setError('');
    let failed = false;
    try {
      const saved = await desktop.setAccentColor(mode, color);
      if (pending.current === 1) setColors(saved);
    } catch {
      failed = true;
      setError('Your color could not be saved. Please try again.');
    } finally {
      pending.current--;
    }
    // The last save in flight failed: show the colors that are saved, not the swatch that wasn't
    // (the ones shown before this choice if they can't be read). A newer choice wins over either.
    if (!failed || pending.current !== 0) return;
    const saved = (await desktop.getAccentColors?.().catch(() => null)) ?? before;
    if (pending.current === 0) setColors(saved);
  };
  return (
    <section className="accent-settings" aria-labelledby="accent-settings-title">
      <header>
        <strong id="accent-settings-title">Accent color</strong>
        <small>A color for each appearance.</small>
      </header>
      <div className="accent-mode-options">
        {(['light', 'dark'] as const).map((mode) => (
          <div className="accent-mode" key={mode}>
            <span className="accent-mode-label">
              {mode === 'light' ? <Sun size={16} /> : <Moon size={16} />}{' '}
              {mode === 'light' ? 'Light' : 'Dark'}
            </span>
            <div
              className="accent-swatches"
              role="group"
              aria-label={`${mode === 'light' ? 'Light' : 'Dark'} accent color`}
            >
              {/* PAIGE: CapturePoint's own orange is not a Paige color (`orange-accent`), and Paige blue,
                  the default everywhere, leads the swatches (paige/paige-theme.ts). */}
              {(hostHas('orange-accent') ? ACCENT_PRESETS[mode] : paigeAccentPresets(mode))
                .filter((preset) => preset.name !== 'Orange' || hostHas('orange-accent'))
                .map((preset) => (
                  <TooltipButton
                    key={preset.name}
                    type="button"
                    className="accent-swatch"
                    style={{ backgroundColor: preset.color }}
                    aria-label={preset.name}
                    tooltip={preset.name}
                    aria-pressed={colors[mode] === preset.color}
                    onClick={() => void choose(mode, preset.color)}
                  >
                    {colors[mode] === preset.color && <Check size={17} aria-hidden="true" />}
                  </TooltipButton>
                ))}
              <label className="accent-custom">
                <input
                  type="color"
                  aria-label={`Custom ${mode} accent color`}
                  value={colors[mode]}
                  onChange={(event) => void choose(mode, event.target.value)}
                />
                <span>Custom</span>
              </label>
            </div>
          </div>
        ))}
      </div>
      {error && (
        <p
          className="appearance-error"
          role="alert"
          data-telemetry={
            error === COLORS_UNREADABLE
              ? 'error:appearance-load-failed'
              : 'error:appearance-save-failed'
          }
        >
          {error}
        </p>
      )}
    </section>
  );
}
